import { FC, useEffect, useState } from 'react';
import Plot from 'react-plotly.js';
import { MessageCircleX, Clock, Loader2 } from 'lucide-react';
import { TQuizOverviewAnalytics } from '../../../../types/analytics.types';
import AnalyticsService from '../../../../services/analytics.service';

const renderBoldText = (text: string) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={index} className="font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <span key={index}>{part}</span>;
  });
};

interface QuizOverviewProps {
  quizId: string;
}

const QuizOverview: FC<QuizOverviewProps> = ({ quizId }) => {
  const [data, setData] = useState<TQuizOverviewAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    AnalyticsService.fetchQuizOverviewAnalytics({ quizId })
      .then((res: { data: { data: TQuizOverviewAnalytics | null } }) => {
        setData(res.data?.data ?? null);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [quizId]);

  if (loading) {
    return (
      <div className="mt-6 w-full flex justify-center items-center py-20">
        <Loader2 className="animate-spin text-gray-400" size={40} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mt-6 w-full flex flex-col justify-center items-center py-20">
        <MessageCircleX color="#999" size={60} />
        <p className="text-[14px] font-medium text-appTextLight mt-3">
          No overview data available.
        </p>
      </div>
    );
  }

  const { averageQuestionData, averageTime, quizInsights } = data;

  return (
    <div className="mt-6 w-full flex gap-4">
      {/* Left column — pie chart + average time */}
      <div className="w-1/2 flex flex-col gap-4">
        <div
          className="
            border-[#E2E8F0] border bg-white px-4 hover:border-[#020817] transition-all
            cursor-pointer rounded-2xl w-full
          "
        >
          <Plot
            data={[
              {
                type: 'pie',
                values: [
                  averageQuestionData.totalCorrect,
                  averageQuestionData.totalIncorrect,
                ],
                labels: ['Correct', 'Incorrect'],
                marker: {
                  colors: ['#91c89e', '#ef4444'],
                },
                textinfo: 'label+percent',
                hoverinfo: 'label+value+percent',
                textfont: {
                  color: '#FFFFFF',
                  family: 'Inter, system-ui, sans-serif',
                  size: 12,
                },
                insidetextfont: {
                  color: '#FFFFFF',
                  family: 'Inter, system-ui, sans-serif',
                  size: 12,
                },
              },
            ]}
            layout={{
              title: {
                text: 'Average Question Performance',
                x: 0,
                xanchor: 'left',
                font: {
                  size: 18,
                  family: 'Inter, system-ui, sans-serif',
                },
                subtitle: {
                  text: `${averageQuestionData.totalQuestions} total questions across all attempts`,
                },
              },
              showlegend: true,
              legend: {
                orientation: 'h',
                yanchor: 'bottom',
                y: -0.3,
                xanchor: 'center',
                x: 0.5,
              },
              margin: {
                t: 100,
                b: 100,
                l: 20,
                r: 20,
              },
              plot_bgcolor: '#fff',
              paper_bgcolor: '#fff',
              autosize: true,
            }}
            className="h-[400px] w-full"
            style={{ width: '100%', maxWidth: '100%' }}
            config={{
              responsive: true,
              displayModeBar: false,
            }}
            useResizeHandler={true}
          />
        </div>

        {/* Average Time Card */}
        <div className="border-[#E2E8F0] border bg-white px-4 py-4 hover:border-[#020817] transition-all cursor-pointer rounded-2xl flex items-center gap-3">
          <Clock size={24} className="text-gray-500" />
          <div>
            <p className="text-[12px] text-appTextLight font-medium">
              Average Completion Time
            </p>
            <p className="text-[20px] font-semibold">
              {averageTime.formattedTime}
            </p>
            <p className="text-[12px] text-appTextLight">
              across {averageTime.totalStudents} student{averageTime.totalStudents !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>

      {/* Right column — insights */}
      <div className="w-1/2">
        <div
          className="border-[#E2E8F0] border bg-white px-4 hover:border-[#020817] transition-all
            cursor-pointer rounded-2xl w-full py-5"
        >
          <h4 className="font-medium text-[16px]">AI interaction insights</h4>

          <div className="mt-4 space-y-2 max-h-[440px] overflow-y-auto">
            {quizInsights.length > 0 ? (
              quizInsights.map((insight, idx) => (
                <div
                  key={idx}
                  className="bg-[#f9fafc] rounded-lg px-4 py-3 text-[13px] text-[#333]"
                >
                  <span className="font-medium mr-2">{idx + 1}.</span>
                  {renderBoldText(insight)}
                </div>
              ))
            ) : (
              <div className="flex flex-col justify-center items-center py-10">
                <MessageCircleX color="#999" size={60} />
                <p className="text-[14px] font-medium text-appTextLight mt-3">
                  No insights available yet.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizOverview;
